import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Input from '#lib/components/input.jsx';
import Button from '#lib/components/button.jsx';
import { HoursModal } from '#components/hours-modal.jsx';
import { formatHour } from '#@itinerary/planner/helpers.js';
import { DetailHeading } from '#@itinerary/planner/detail.jsx';
import { Icon } from '@iconify-icon/react';
import FloatingTooltip from '#components/tooltip.jsx';
import MiniPopUp from './mini-popup.jsx';
import AiDescription from '#components/ai-description.jsx';
import Select from '#lib/components/select.jsx';
import { defaultMakeReactKey } from '#helpers/helpers.js';
import { GalleryEditor } from '#@itinerary/gallery/gallery-editor.jsx';
import { useGallery } from '#@itinerary/gallery/use-gallery.js';
import { ItineraryContext } from '#routes/manage/itinerary/edit.jsx';
import HTMLEditor from '#components/html-editor.jsx';
import { AddressModal } from '#components/address-modal.jsx';

export function ItemDetail(props) {
    const {
        location,
        itemType,
        onEdit,
        onMoveGallery,
        onEditHours,
        onCreate,
        onLock,
        totalDays,
        plans,
        hasError,
        handleError,
        invalidFields,
        data,
        setUnsavedChanges,
        ...rest
    } = props;

    const { language, themeId } = useContext(ItineraryContext);
    const { id, gallery, filterId, name, description } = data;
    const titleRef = useRef();
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        const title = titleRef.current;
        if (title) title.focus();
    }, []);

    const [isOpen, setIsOpen] = useState(false);

    const setDescription = useCallback(
        (newDescription) => {
            onEdit(location, itemType, 'description', newDescription, id);
        },
        [onEdit, location, itemType, id]
    );

    const onChange = useCallback(
        (event, hours) => {
            if (hours) {
                onEdit(location, itemType, 'openingHours', hours, id);
            }
            const { name, value } = event.target;
            if (hasError?.at(0)?.field === name) handleError({ placeId: id, field: name });
            if (itemType === 'info') onEdit(location, itemType, name, value, id);
            else onEdit(location, itemType, name, value, id);
        },
        [onEdit, location, itemType, id, hasError]
    );

    const onChangeSelect = useCallback(
        (filter) => {
            onEdit(location, itemType, 'filterId', filter?.id, id);
            onEdit(location, itemType, 'filterKey', filter?.filterKey, id);
            onEdit(location, itemType, 'filterName', filter?.name, id);
        },
        [onEdit, location, itemType, id]
    );

    const createPlan = useCallback(
        (type) => {
            onCreate(totalDays - 1, data, type, true);
            onLock(location, itemType, 'locked', true, id);
        },
        [onCreate, totalDays, name, description]
    );

    const galleryProps = useGallery({
        initialData: gallery,
        onAdd: () => {
            onEdit(location, itemType, 'imgLoading', true, id);
        },
        onAddImage: (state) => {
            onEdit(location, itemType, 'gallery', state, id);
        },
        onEdit: (state) => {
            onEdit(location, itemType, 'gallery', state, id);
        },
        onUpload: (state) => {
            const update = state.find((el) => el.src.startsWith('blob'));
            if (update) return;
            onEdit(location, itemType, 'imgLoading', false, id);
            onEdit(location, itemType, 'gallery', state, id);
        },
        onDelete: (state) => {
            onEdit(location, itemType, 'gallery', state, id);
        },
        setUnsavedChanges
    });

    return (
        <div id={id} className="relative flex flex-col gap-4 px-4 h-full overflow-auto">
            <DetailHeading>
                <span>{name}</span>
                <FloatingTooltip content={_t.edit.places.createPlanDescription()}>
                    <Icon
                        icon="line-md:plus-square"
                        width="1.3rem"
                        height="1.3rem"
                        className="pt-1 cursor-pointer"
                        onClick={() => setShowPopup(true)}
                    />
                </FloatingTooltip>
            </DetailHeading>

            {itemType === 'suggestions' && <GalleryEditor {...galleryProps} onMoveGalleryPlace={onMoveGallery}
                location={location}
                type={itemType}
                placeId={id}
            />}

            <Input.Full
                ref={titleRef}
                label={_t.edit.fields.title()}
                value={name || ''}
                name="name"
                onChange={onChange}
                hasError={hasError && hasError?.at(0)?.field === 'name'}
            />

            <div>
                <AiDescription
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    setAiDescription={setDescription}
                    name={name}
                    city={location}
                    contextName="place"
                    language={language}
                    themeId={themeId}
                />
                <HTMLEditor
                    onEditorChange={(event, editor) =>
                        onChange({ target: { name: 'description', value: editor.getContent() } })
                    }
                    value={description}
                />
            </div>

            {itemType === 'suggestions' && (
                <SuggestionDetail
                    id={id}
                    onChange={onChange}
                    onChangeSelect={onChangeSelect}
                    filterId={filterId}
                    {...props}
                />
            )}

            <MiniPopUp isOpen={showPopup} onClose={() => setShowPopup(false)} type={itemType} addPlan={createPlan} />
        </div>
    );
}

export function SuggestionDetail(props) {
    const { id, filterId, totalFilters, onChange, onChangeSelect, data } = props;
    const { address, distance, rating, openingHours } = data;
    const [showHours, setShowHours] = useState(false);
    const [showHoursModal, setShowHoursModal] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const handleShowMapModal = () => setShowMapModal(true);

    return (
        <div id={`s-${id}`} className="relative flex flex-col gap-4">
            <Select.Full
                options={totalFilters.filter((v) => v.type === 'suggestion')}
                label={_t.edit.fields.category()}
                defaultValue={(filterId && totalFilters.find((el) => el.id == filterId)) || { name: '' }}
                name="category"
                makeReactKey={defaultMakeReactKey}
                displayValue={(v) => _t.edit.filters[v.filterKey]()}
                onChange={onChangeSelect}
            />
            <div className="flex flex-col">
                <label
                    className="mb-1 flex items-baseline gap-2 whitespace-nowrap text-sm font-medium text-neutral-700"
                    htmlFor="address"
                >
                    {_t.edit.fields.address()}
                </label>
                <div className="relative w-full flex gap-2">
                    <Input.Full className="w-full" value={address || ''} name="address" onChange={onChange} />

                    <FloatingTooltip
                        content={_t.edit.openMapPanel()}
                        placement="top"
                        refClassName="absolute right-2 bottom-1 cursor-pointer text-neutral-700 hover:text-primary-600"
                    >
                        <Icon icon="eva:pin-outline" width="1.2rem" height="1.2rem" onClick={handleShowMapModal} />
                    </FloatingTooltip>
                </div>
            </div>
            <div className="flex gap-4">
                <div className="w-1/2">
                    <Input.Full
                        label={_t.edit.fields.distance()}
                        value={distance || '0'}
                        name="distance"
                        type="number"
                        step="0.1"
                        min="0"
                        onChange={onChange}
                    />
                </div>
                <div className="w-1/2">
                    <Input.Full
                        label={_t.edit.fields.rating()}
                        value={rating || '0'}
                        name="rating"
                        type="number"
                        step="1"
                        max="5"
                        min="0"
                        onChange={onChange}
                    />
                </div>
            </div>

            <div className="py-2 pl-2 pr-1 cursor-pointer rounded flex justify-between hover:bg-neutral-100">
                <h3 className="font-bold text-neutral-700 text-md">{_t.edit.filters.openingHours()}</h3>
                <div className="flex gap-2 text-neutral-600">
                    <Button className="hover:text-neutral-900" onClick={() => setShowHoursModal(true)}>
                        <Icon icon="material-symbols:edit-outline-rounded" width="1.2rem" height="1.2rem" />
                    </Button>
                    <Button className="hover:text-neutral-900" onClick={() => setShowHours(!showHours)}>
                        {showHours ? (
                            <Icon
                                icon="material-symbols:remove-rounded"
                                width="1.2rem"
                                height="1.2rem"
                                className="hover:text-neutral-900"
                            />
                        ) : (
                            <Icon
                                icon="material-symbols:add"
                                width="1.2rem"
                                height="1.2rem"
                                className="hover:text-neutral-900"
                            />
                        )}
                    </Button>
                </div>
            </div>

            {showHours && <DayHours openingHours={openingHours} />}
            {showMapModal && (
                <AddressModal
                    isOpen={showMapModal}
                    onClose={() => setShowMapModal(false)}
                    type="suggestion"
                    defaultQuery={address}
                    defaultAddress={address}
                    onChange={onChange}
                />
            )}

            <HoursModal
                suggestionData={data}
                isOpen={showHoursModal}
                onClose={() => setShowHoursModal(false)}
                openingHours={openingHours}
                onChange={onChange}
            />
        </div>
    );
}

function DayHours(props) {
    const { openingHours } = props;
    return (
        <div className="flex-col space-y-4 p-2 bg-white">
            {openingHours?.map((day, idx) => (
                <div key={idx} className="grid grid-cols-12 border-b border-b-neutral-300">
                    <span className="col-span-2 pb-2 font-semibold">{day.day.substring(0, 3)}</span>
                    <div className="col-span-10 flex gap-x-8">
                        {day.periods.length > 0 ? (
                            day.periods.map((period, pidx) => (
                                <div key={pidx} className="flex gap-2">
                                    <span>{formatHour(period?.open) || ''}</span>
                                    {'-'}
                                    <span>{formatHour(period?.close) || ''}</span>
                                </div>
                            ))
                        ) : (
                            <div className="w-full">Closed</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
